import type { Guide } from "./types.js";
import { Lead, H2, P, Ol, Ul, Li, AppLink, Ext, C, Callout, Step } from "./primitives.js";

export const meta = {
  id: "getting-started",
  title: "Getting started",
  category: "Getting started",
  lastUpdated: "2026-05-23",
  description: "First-time setup — what to install, which credentials to wire up, and how to verify everything works.",
  relatedGuides: ["credentials-reference", "produce-article"],
  readTimeMin: 6,
} as const;

export function Body() {
  return (
    <>
      <Lead>
        This is the AlternaBe Content Machine — an AI-driven pipeline that produces SEO-optimised Hebrew articles
        and distributes them across 8 channels (Telegram, Brevo email, YouTube, Facebook, Instagram, WhatsApp, X, TikTok).
        Most operator work happens in this UI; behind the scenes the <C>keywords/</C> package runs the orchestration.
      </Lead>

      <H2>Quick orientation</H2>
      <P>The five surfaces you'll use most:</P>
      <Ul>
        <Li><AppLink to="overview">Overview</AppLink> — KPIs + activity feed + clusters + recent articles. Your daily landing page.</Li>
        <Li><AppLink to="pipeline">Pipeline</AppLink> — the heart of the app. Cluster strip + keyword table + per-keyword detail panel with all the action buttons.</Li>
        <Li><AppLink to="tasks">Tasks</AppLink> — auto-derived inbox of what needs your attention next.</Li>
        <Li><AppLink to="content-db">Content DB</AppLink> — local mirror of live Sanity content; feeds the internal-link resolver.</Li>
        <Li><AppLink to="credentials">Credentials</AppLink> — every API key + integration, with smoke tests and setup guides per card.</Li>
      </Ul>

      <H2>One-time setup</H2>
      <P>
        Most integrations have their own setup guide on their Credentials card (click any card with a "Setup instructions" section). Below is the order that gets you to a working pipeline the fastest.
      </P>

      <Step n={1} title="Verify the must-haves are connected">
        <P>Open <AppLink to="credentials">Credentials</AppLink>. These four are essential for the pipeline to work at all:</P>
        <Ul>
          <Li><strong>Claude (Anthropic / Bedrock)</strong> — brief generation + article production + classifier. The most critical credential.</Li>
          <Li><strong>SerpAPI</strong> — SERP snapshots (top competitors, PAA, related queries).</Li>
          <Li><strong>Firecrawl</strong> — scrapes competitor articles for the brief generator.</Li>
          <Li><strong>Sanity (alternabe-cms)</strong> — read-only token for the Content DB. Without this, internal links fall back to local-keyword data only.</Li>
        </Ul>
        <P>Each card has a <strong>Test</strong> button. All four should report green before producing anything.</P>
      </Step>

      <Step n={2} title="Sync the Content DB">
        <P>
          Once Sanity is green, open <AppLink to="content-db">Content DB</AppLink> and click <strong>Refresh now</strong>.
          This pulls ~110 live articles + treatment pages from alternabe-cms, classifies each into our cluster map (Haiku), and extracts topic tags. ~30–60s + ~$0.20 first-sync cost. Future syncs are incremental.
        </P>
        <Callout kind="tip">
          A nightly cron auto-syncs at 03:00 IL. You only need to hit Refresh manually for one-off updates (e.g. just published a new article and want it surfaced in internal links immediately).
        </Callout>
      </Step>

      <Step n={3} title="(Optional) Wire up Replicate for image + video generation">
        <P>
          <strong>Replicate</strong> credential unlocks the image + video pipeline (Flux Pro for hero/social, Wan 2.2 for short clips). Without it, articles produce without media — fine for text-only Telegram/Brevo/X but blocks IG/TikTok which need media.
        </P>
      </Step>

      <Step n={4} title="(Optional) Wire distribution channels you'll use">
        <P>
          Each channel has its own setup guide (often involving Meta Business Manager, OAuth flows, app reviews). Don't try to set them all up at once. Start with the ones you'll actually use this week:
        </P>
        <Ul>
          <Li><strong>Telegram</strong> — easiest. Just a bot token + channel id. Free.</Li>
          <Li><strong>Brevo</strong> — email already validated against ~487 contacts. Set if not configured.</Li>
          <Li><strong>Meta (FB + IG)</strong> — one auth covers both. ~1-2 hours setup.</Li>
          <Li><strong>WhatsApp Business</strong> — separate auth. Requires template approval (~24h) for broadcasts.</Li>
          <Li><strong>YouTube</strong> — needs external <Ext href="https://github.com/itayshmool/shmool-youtube-mcp">shmool-youtube-mcp</Ext> clone.</Li>
          <Li><strong>X (Twitter)</strong> — Basic tier paid ($200/mo).</Li>
          <Li><strong>TikTok</strong> — one-time external OAuth flow.</Li>
        </Ul>
      </Step>

      <Step n={5} title="Produce your first article">
        <P>
          See the <AppLink to="help?guide=produce-article">From keyword to published article</AppLink> guide — walks through the full Enrich → Scrape → Brief → Produce → Distribute flow on one keyword.
        </P>
      </Step>

      <H2>Cost expectations</H2>
      <P>At the spec's target rate of 4-7 articles per week:</P>
      <Ul>
        <Li><strong>Claude (article production)</strong>: ~$0.23 per article on Sonnet 4.6 with caching. ~$3-6/week.</Li>
        <Li><strong>SerpAPI</strong>: free tier (250 searches/mo) or ~$50/mo paid.</Li>
        <Li><strong>Firecrawl</strong>: free tier covers low volume.</Li>
        <Li><strong>Distribution LLM costs</strong>: <strong>$0</strong> — captions are heuristic, not LLM-generated.</Li>
        <Li><strong>External subscriptions</strong>: only X API is paid ($200/mo). Everything else is free or freemium.</Li>
      </Ul>
      <Callout kind="tip" title="AWS credits">
        If you have AWS Bedrock credits, flip the Provider dropdown in the Claude card to "AWS Bedrock" — every LLM call routes through Bedrock at the same per-token price, but debits AWS credits instead of your Anthropic balance. See the Cost optimization guide.
      </Callout>

      <H2>Common gotchas</H2>
      <Ol>
        <Li><strong>Hebrew RTL rendering</strong> in the UI is already wired (<C>dir="auto"</C>). If text looks broken, refresh the page — caching can be sticky.</Li>
        <Li><strong>Israeli HMO portals are WAF-blocked</strong> on free-tier Firecrawl. The competitor scraper auto-filters them out so it doesn't matter for normal use.</Li>
        <Li><strong>SerpAPI free tier returns cached SERPs</strong>. For accurate rank tracking, cross-validate with DataForSEO.</Li>
      </Ol>
    </>
  );
}

export const guide: Guide = { meta, Body };
